import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../../../apiFetch';
import { BASE_URL } from '../../../../config';
import './AdminContractTermination.css';

const AdminContractTermination = () => {
    const [contracts, setContracts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');

    // 🔁 Tabs
    const [activeTab, setActiveTab] = useState('active');

    // 📝 termination reasons keyed by contractId
    const [terminationReasons, setTerminationReasons] = useState({});

    /* =========================
       ROLE HELPERS
    ========================= */
    const resolveRole = (contract) => (contract.signedByRole || '').toLowerCase();

    const formatRole = (contract) => {
        const role = resolveRole(contract);
        return role ? role.charAt(0).toUpperCase() + role.slice(1) : '—';
    };

    const isContractExpired = (contract) => {
        if (!contract.adminApprovalTime || !contract.contractDuration) return false;

        const durationMs = (() => {
            const d = contract.contractDuration.toLowerCase();
            const n = parseInt(d, 10);
            if (Number.isNaN(n)) return 0;

            if (d.includes('year')) return n * 365 * 24 * 60 * 60 * 1000;
            if (d.includes('month')) return n * 30 * 24 * 60 * 60 * 1000;
            if (d.includes('day')) return n * 24 * 60 * 60 * 1000;
            if (d.includes('hour')) return n * 60 * 60 * 1000;
            if (d.includes('minute')) return n * 60 * 1000;
            return 0;
        })();

        if (!durationMs) return false;

        const expiry =
            new Date(contract.adminApprovalTime).getTime() + durationMs;

        return Date.now() > expiry;
    };

    const getContractDocUrl = (rawUrl) => {
        if (!rawUrl) return null;

        // already absolute
        if (/^https?:\/\//i.test(rawUrl)) return rawUrl;

        // relative -> backend base
        return `${BASE_URL}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;
    };

    // ✅ Player profile helper (adds "View Profile" for player)
    const getPlayerProfileUrl = (contract) => {
        const p = contract.PlayerDatum;
        if (!p) return null;

        const playerSlug = p.profileSlug || p.slug;
        return playerSlug
            ? `${window.location.origin}/player-profile/${playerSlug}`
            : null;
    };

    const getRoleProfileInfo = (contract) => {
        const role = resolveRole(contract);
        const label = formatRole(contract);

        if (role === 'agent' && contract.Agent) {
            const agentSlug =
                contract.Agent.profileSlug || contract.Agent.slug;

            return {
                label,
                fullName: contract.Agent.fullName || `ID ${contract.agentId}`,
                username: contract.Agent.username,
                profileUrl: agentSlug
                    ? `${window.location.origin}/agent-profile/${agentSlug}`
                    : null,
            };
        }

        if (role === 'scout' && contract.Scout) {
            const scoutSlug =
                contract.Scout.profileSlug || contract.Scout.slug;

            return {
                label,
                fullName: contract.Scout.fullName || `ID ${contract.scoutId}`,
                username: contract.Scout.username,
                profileUrl: scoutSlug
                    ? `${window.location.origin}/scout-profile/${scoutSlug}`
                    : null,
            };
        }

        if (role === 'club' && contract.Club) {
            const clubSlug =
                contract.Club.profileSlug || contract.Club.slug;

            return {
                label,
                fullName:
                    contract.Club.clubName ||
                    contract.Club.fullName ||
                    `ID ${contract.clubId}`,
                username: contract.Club.username,
                profileUrl: clubSlug
                    ? `${window.location.origin}/club-profile/${clubSlug}`
                    : null,
            };
        }

        return {
            label: '—',
            fullName: '—',
            username: null,
            profileUrl: null,
        };
    };

    /* =========================
       FETCH
    ========================= */
    const fetchContracts = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await apiFetch('/api/admin/contracts');
            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();
            setContracts(Array.isArray(data) ? data : []);

        } catch (err) {
            console.error(err);
            setError('Failed to load contracts.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchContracts();
    }, []);

    /* =========================
       TERMINATE
    ========================= */
    const handleEndContract = async (contractId) => {
        const terminationReason = terminationReasons[contractId];

        if (!terminationReason || !terminationReason.trim()) {
            alert('Please provide a termination reason.');
            return;
        }

        if (!window.confirm('Are you sure you want to terminate this contract?')) {
            return;
        }

        try {
            const res = await apiFetch(`/api/admin/contracts/${contractId}/end-now`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ terminationReason }),
            });
            if (!res.ok) throw new Error(await res.text());

            await fetchContracts();

            setTerminationReasons((prev) => {
                const copy = { ...prev };
                delete copy[contractId];
                return copy;
            });
        } catch (err) {
            console.error(err);
            alert(err.message || 'Failed to terminate contract.');
        }
    };

    /* =========================
       REINSTATE
    ========================= */
    const handleReinstateContract = async (contractId) => {
        if (
            !window.confirm(
                'Are you sure you want to reinstate this contract?'
            )
        ) {
            return;
        }

        try {
            const res = await apiFetch(`/api/admin/contracts/${contractId}/reinstate`, {
                method: 'PUT',
            });
            if (!res.ok) throw new Error(await res.text());

            await fetchContracts();
        } catch (err) {
            console.error(err);
            alert(err.message || 'Failed to reinstate contract.');
        }
    };

    /* =========================
       FILTERING (CANONICAL)
    ========================= */

    // 🟢 ACTIVE = admin approved + not terminated/closed/rejected
    const activeContracts = contracts.filter(
        (c) =>
            c.adminApproval === 'approved' &&
            !['terminated', 'closed', 'rejected'].includes(c.status)
    );

    // ⚫ INACTIVE = terminated ONLY
    const inactiveContracts = contracts.filter(
        (c) => c.status === 'terminated'
    );

    const applySearch = (list) =>
        list.filter((c) => {
            if (!search.trim()) return true;
            const term = search.toLowerCase();

            return (
                c.PlayerDatum?.fullName?.toLowerCase().includes(term) ||
                c.PlayerDatum?.username?.toLowerCase().includes(term) ||
                c.Agent?.fullName?.toLowerCase().includes(term) ||
                c.Agent?.username?.toLowerCase().includes(term) ||
                c.Scout?.fullName?.toLowerCase().includes(term) ||
                c.Scout?.username?.toLowerCase().includes(term) ||
                c.Club?.clubName?.toLowerCase().includes(term) ||
                c.Club?.username?.toLowerCase().includes(term) ||
                String(c.id).includes(term)
            );
        });

    const displayedContracts =
        activeTab === 'active'
            ? applySearch(activeContracts)
            : applySearch(inactiveContracts);

    /* =========================
       RENDER
    ========================= */
    return (
        <div className="admin-contract-termination">
            <h2 className="title">Contract Management</h2>

            {/* 🔘 TAB TOGGLE */}
            <div className="tab-toggle">
                <button
                    className={activeTab === 'active' ? 'active' : ''}
                    onClick={() => setActiveTab('active')}
                >
                    Active Contracts
                </button>
                <button
                    className={activeTab === 'inactive' ? 'active' : ''}
                    onClick={() => setActiveTab('inactive')}
                >
                    Inactive Contracts
                </button>
            </div>

            {/* 🔍 SEARCH */}
            <div className="search-bar">
                <input
                    type="text"
                    placeholder={
                        activeTab === 'active'
                            ? 'Search active contracts...'
                            : 'Search inactive contracts...'
                    }
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="contract-search-input"
                />
            </div>

            {loading && <p>Loading...</p>}
            {error && <p className="error">{error}</p>}

            {!loading && displayedContracts.length === 0 && (
                <div className="placeholder-message">
                    No {activeTab === 'active' ? 'active' : 'inactive'} contracts found.
                </div>
            )}

            {displayedContracts.map((contract) => {
                const roleInfo = getRoleProfileInfo(contract);
                const playerProfileUrl = getPlayerProfileUrl(contract);

                return (
                    <div className="contract-card" key={contract.id}>
                        <p><strong>Contract ID:</strong> {contract.id}</p>

                        <p>
                            <strong>Player:</strong>{' '}
                            {contract.PlayerDatum?.fullName}
                            {contract.PlayerDatum?.username &&
                                ` (@${contract.PlayerDatum.username})`}
                            {playerProfileUrl && (
                                <a
                                    href={playerProfileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {' '}View Profile
                                </a>
                            )}
                        </p>

                        <p>
                            <strong>Role:</strong>{' '}
                            {roleInfo.label} – {roleInfo.fullName}
                            {roleInfo.username && ` (@${roleInfo.username})`}
                            {roleInfo.profileUrl && (
                                <a
                                    href={roleInfo.profileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {' '}View Profile
                                </a>
                            )}
                        </p>

                        <p><strong>Duration:</strong> {contract.contractDuration}</p>
                        <p><strong>Notes:</strong> {contract.notes || '—'}</p>

                        {contract.agentContractUrl && (
                            <p>
                                <strong>Contract Document:</strong>{' '}
                                <a
                                    href={getContractDocUrl(contract.agentContractUrl)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    View PDF
                                </a>
                            </p>
                        )}

                        <p>
                            <strong>Admin Approved At:</strong>{' '}
                            {contract.adminApprovalTime
                                ? new Date(contract.adminApprovalTime).toLocaleString()
                                : '—'}
                        </p>

                        <p>
                            <strong>Signed By Player:</strong>{' '}
                            {contract.signedByPlayer ? '✅ Yes' : '❌ No'}
                        </p>

                        <p>
                            <strong>Agreed To Terms:</strong>{' '}
                            {contract.agreeToTerms ? '✅ Yes' : '❌ No'}
                        </p>

                        {/* 🔴 ACTIVE TAB ACTIONS */}
                        {activeTab === 'active' && (
                            <>
                                <div className="termination-reason">
                                    <label><strong>Termination Reason:</strong></label>
                                    <textarea
                                        placeholder="Enter termination reason..."
                                        value={terminationReasons[contract.id] || ''}
                                        onChange={(e) =>
                                            setTerminationReasons((prev) => ({
                                                ...prev,
                                                [contract.id]: e.target.value,
                                            }))
                                        }
                                    />
                                </div>

                                <button
                                    className="terminate-btn"
                                    onClick={() => handleEndContract(contract.id)}
                                >
                                    🔚 End Contract Now
                                </button>
                            </>
                        )}

                        {/* 🔄 INACTIVE TAB ACTION */}
                        {activeTab === 'inactive' &&
                            contract.adminApproval === 'approved' &&
                            !isContractExpired(contract) && (
                                <button
                                    className="terminate-btn"
                                    onClick={() => handleReinstateContract(contract.id)}
                                >
                                    🔄 Reinstate Contract
                                </button>
                            )}

                    </div>
                );
            })}
        </div>
    );
};

export default AdminContractTermination;












